import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import UseContext from '../../../../../../hooks/useContext';
import ApiDatabase from '../../../../../../server';
import Usefetchdata from '../../../../../../hooks/usefetchdata';
import Banner from '../../../../../banner/banner';
import Divider from '../../../../../divider/divider';
import TextSelectNationality from '../../../../../textSelect/components/textSelectNationality';
import ModalBody from '../../../../components/body';
import UseModal from '../../../../useModal';

import {
  DocumentViewer,
  DocumentSidebar,
  ModalHeader,
  UserInfoGrid,
  ValidationActions,
  DateInput,
  useAdminModalFiles,
  useAdminModalTempWorker,
  useAllUserDocuments,
  formatDateToString,
  parseDateFromAPI,
  openNextUnverifiedDocument,
} from '../shared';

const AdminModalFileAcceptIdentity = () => {
  const { toggle, switchModal } = UseModal();
  const {
    adminType,
    adminTempWorkerId,
    adminUserId,
    adminFileName,
    setUpdateTempWorker,
    setAdminType,
  } = UseContext();
  const { userData } = Usefetchdata();
  
  const [numberIdentity, setNumberIdentity] = useState('');
  const [placeOfBirthday, setPlaceOfBirthday] = useState('');
  const [dateObtained, setDateObtained] = useState(null);
  const [dateExpiration, setDateExpiration] = useState(null);
  const [nationality, setNationality] = useState('');
  const [nationalityList, setNationalityList] = useState([]);
  
  const token = localStorage.getItem('xsrfToken');

  // Hooks centralisés
  const { docs, isWaiting } = useAdminModalFiles({
    token,
    adminUserId,
    adminType,
    adminFileName,
    enabled: !!userData._id && !!adminUserId && !!adminType,
  });

  const { tempWorker, user } = useAdminModalTempWorker({
    token,
    adminTempWorkerId,
    enabled: !!userData._id && !!adminTempWorkerId,
  });

  // Récupérer tous les documents de l'utilisateur pour la sidebar
  const { allDocuments, isLoading: isLoadingAllDocs } = useAllUserDocuments({
    token,
    adminUserId,
    currentType: adminType,
    enabled: !!userData._id && !!adminUserId && !isWaiting,
  });

  // Calcul de la date de fin de validité suggérée (+10 ans - 1 jour)
  const getSuggestedExpiration = (obtainedDate) => {
    if (!obtainedDate) return null;
    const date = new Date(obtainedDate);
    date.setFullYear(date.getFullYear() + 10);
    date.setDate(date.getDate() - 1);
    return date;
  };

  // Calcul de la date d'obtention suggérée (-10 ans + 1 jour)
  const getSuggestedObtained = (expirationDate) => {
    if (!expirationDate) return null;
    const date = new Date(expirationDate);
    date.setFullYear(date.getFullYear() - 10);
    date.setDate(date.getDate() + 1);
    return date;
  };

  // Charger les données existantes du tempWorker
  useEffect(() => {
    if (tempWorker._id) {
      setNumberIdentity(tempWorker.identity?.number_identity ?? '');
      setPlaceOfBirthday(tempWorker.identity?.birthplace ?? '');
      setDateObtained(
        tempWorker.identity?.start_validity
          ? parseDateFromAPI(tempWorker.identity.start_validity)
          : null
      );
      setDateExpiration(
        tempWorker.identity?.end_validity
          ? parseDateFromAPI(tempWorker.identity.end_validity)
          : null
      );
      setNationality(tempWorker.identity?.nationality ?? '');
    }
  }, [tempWorker]);

  // Charger la liste des nationalités
  useEffect(() => {
    if (token) {
      ApiDatabase.getNationalities(
        { token },
        (data) => setNationalityList(data),
        (err) => {
          if (err.name !== 'CanceledError') console.log(err);
        }
      );
    }
  }, [token]);

  const selectNationality = (nationalitySelected) => {
    setNationality(nationalitySelected._id);
  };

  const modalAcceptSubmit = () => {
    // Vérifier que tous les champs sont remplis
    if (
      !token ||
      !adminTempWorkerId ||
      !numberIdentity ||
      !placeOfBirthday ||
      !dateObtained ||
      !dateExpiration ||
      !nationality
    ) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    // Vérifier que la date de fin de validité est supérieure à la date d'obtention
    const obtainedDate = new Date(dateObtained);
    const expirationDate = new Date(dateExpiration);
    
    obtainedDate.setHours(0, 0, 0, 0);
    expirationDate.setHours(0, 0, 0, 0);
    
    if (expirationDate <= obtainedDate) {
      toast.error('La date de fin de validité doit être supérieure à la date d\'obtention');
      return;
    }

    ApiDatabase.postAcceptTempWorkerFileIdentityAdmin(
      {
        token,
        adminTempWorkerId,
        adminType,
        numberIdentity,
        placeOfBirthday,
        dateObtained,
        dateExpiration,
        idNationality: nationality?._id || nationality,
      },
      (data) => {
        toast.success("Fichier d'identité accepté avec succès");
        setUpdateTempWorker({ tempWorkerId: data._id, documentType: adminType });
        
        // Ouvrir le prochain document non vérifié
        openNextUnverifiedDocument({
          tempWorker,
          allDocuments,
          currentType: adminType,
          setAdminType,
          switchModal,
          toggle,
        });
      },
      (err) => {
        if (err.name !== 'CanceledError') console.log(err);
      }
    );
  };

  const handleRefuse = () => {
    switchModal('ad_file_refuse');
  };

  const handleOpenDocumentModal = (type, modalName) => {
    setAdminType(type);
    switchModal(modalName);
  };

  return (
    <ModalBody>
      <div className="flex gap-4 h-[750px]">
        {/* Section 1: Autres documents */}
        <DocumentSidebar
          allDocuments={allDocuments}
          isLoading={isLoadingAllDocs}
          currentType={adminType}
          onOpenDocumentModal={handleOpenDocumentModal}
          tempWorker={tempWorker}
        />
        
        {/* Section 2: Document Viewer */}
        <div className="flex-1 min-w-0 bg-gray-50 rounded-lg overflow-hidden">
          <DocumentViewer docs={docs} isWaiting={isWaiting} />
        </div>
        
        {/* Section 3: User Info + Inputs + Boutons */}
        <div className="w-80 flex flex-col gap-3 bg-gray-50 rounded-lg p-4 overflow-y-auto">
          <ModalHeader
            adminType={adminType}
            tempWorker={tempWorker}
            docsCount={docs.length}
            onClose={toggle}
          />
          
          <UserInfoGrid
            user={user}
            validationStatus={tempWorker.identity?.validated}
            reason={tempWorker.identity?.reason}
            showNationality={true}
            nationalityText={tempWorker.visa?.type ? 'Hors UE' : 'UE, EE, Suisse'}
            additionalBanner={
              tempWorker.visa?.validated === '1' && (
                <div className="col-span-2">
                  <Banner type="warning">
                    Un {tempWorker.visa?.type === 'Visa' ? 'visa' : 'titre de séjour'}{' '}
                    devra aussi être vérifié pour valider ce candidat.
                  </Banner>
                </div>
              )
            }
          />
          
          <Divider />
          
          <div className="w-full">
            <label className="ft-sm text-gray-500">
              <div>N° de la pièce d'identité</div>
              <input
                type="text"
                className="w-full h-10 pl-3 pr-3 ft-b border rounded-lg text-gray-900 uppercase"
                value={numberIdentity}
                onChange={(e) => setNumberIdentity(e.target.value.toUpperCase())}
              />
            </label>
          </div>
          
          <div className="w-full">
            <label className="ft-sm text-gray-500">
              <div>Lieu de naissance</div>
              <input
                type="text"
                className="w-full h-10 pl-3 pr-3 ft-b border rounded-lg text-gray-900 uppercase"
                value={placeOfBirthday}
                onChange={(e) => setPlaceOfBirthday(e.target.value)}
              />
            </label>
          </div>
          
          <DateInput
            label="Date d'obtention"
            value={dateObtained}
            onChange={setDateObtained}
            suggestion={
              dateExpiration && !dateObtained && (
                <div
                  onClick={() => setDateObtained(getSuggestedObtained(dateExpiration))}
                  className="mt-1 inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 text-xs rounded-full cursor-pointer hover:bg-blue-100 transition-colors"
                >
                  <span>Suggéré : {formatDateToString(getSuggestedObtained(dateExpiration))}</span>
                </div>
              )
            }
          />
          
          <DateInput
            label="Date de fin de validité"
            value={dateExpiration}
            onChange={setDateExpiration}
            suggestion={
              dateObtained && !dateExpiration && (
                <div
                  onClick={() => setDateExpiration(getSuggestedExpiration(dateObtained))}
                  className="mt-1 inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 text-xs rounded-full cursor-pointer hover:bg-blue-100 transition-colors"
                >
                  <span>Suggéré : {formatDateToString(getSuggestedExpiration(dateObtained))}</span>
                </div>
              )
            }
          />
          
          <div className="w-full">
            <TextSelectNationality
              dataList={nationalityList}
              onSelect={selectNationality}
              setData={nationality}
            />
          </div>
          
          <ValidationActions onRefuse={handleRefuse} onAccept={modalAcceptSubmit} />
        </div>
      </div>
    </ModalBody>
  );
};

export default AdminModalFileAcceptIdentity;
